import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EndedAssignmentsListHeaderSortKeys } from '../../_enums/base-customer-header-sort-keys.enum';
import {
  AssignmentCustomerResponse,
  BASE_PERMISSIONS,
  baseAdministrationSelectors,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseAvailableFileTypeEnum,
  BaseButtonSize,
  BaseCoreState,
  BaseModalService,
  BaseOrderDirectionEnum,
  BaseRolesService,
  CustomerProfileAssignmentItemResponse,
  CustomerResponse,
} from 'base';

@Component({
  selector: 'base-customer-employee-master-data-tab',
  templateUrl: './base-customer-employee-master-data-tab.component.html',
})
export class BaseCustomerEmployeeMasterDataTabComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('idCardViewerTemplate') idCardViewerTemplate: ElementRef;
  @Input() assignment: AssignmentCustomerResponse;
  endedAssignments: Array<CustomerProfileAssignmentItemResponse>;

  listHeaderSortKeys = EndedAssignmentsListHeaderSortKeys;
  orderDirection: BaseOrderDirectionEnum;
  orderKey: number;
  directions = BaseOrderDirectionEnum;
  buttonSizes = BaseButtonSize;
  baseAvailableFileTypeEnum = BaseAvailableFileTypeEnum;
  canEditPersonalId: boolean;
  isInternalPersonalIdEdited = false;
  isInternalPersonalIdFormSubmitted = false;
  internalPersonalIdForm = this.fb.group({
    internalPersonalId: ['', Validators.required],
  });
  modalRef: NgbModalRef;
  idCardModalRef: NgbModalRef;
  activeDocumentSrc: Blob;
  idCardFileName: string;
  isTerminalFeatureActive: boolean;
  private destroy$ = new Subject();

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private rolesService: BaseRolesService,
    private modalService: BaseModalService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.assignment !== undefined) {
      this.isInternalPersonalIdEdited = false;
      this.internalPersonalIdForm.patchValue({
        internalPersonalId: this.assignment.profile?.customerProfilePersonalId || '',
      });
      this.store.dispatch(baseAssignmentsActions.getCandidateEndedAssignments({ payload: this.assignment.profile.id }));

      this.store
        .select(baseAssignmentsSelectors.getCandidateEndedAssignments)
        .pipe(
          takeUntil(this.destroy$),
          filter((endedAssignmentsState) => endedAssignmentsState.ok === true),
          map((endedAssignmentsState) => endedAssignmentsState.data)
        )
        .subscribe((endedAssignments) => {
          this.endedAssignments = endedAssignments || [];
        });
    }
  }

  ngOnInit(): void {
    this.canEditPersonalId = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS);
    this.subscribeCustomerData();
    this.subscribeProfileIDCard();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  updateInternalPersonalId(): void {
    this.isInternalPersonalIdFormSubmitted = true;
    if (this.internalPersonalIdForm.valid && this.canEditPersonalId) {
      this.store.dispatch(
        baseAssignmentsActions.updateCustomerProfilePersonalId({
          payload: {
            profile: this.assignment.profile.id,
            assignment: this.assignment.id,
            body: {
              personalId: this.internalPersonalIdForm.get('internalPersonalId')?.value,
            },
          },
        })
      );
      this.isInternalPersonalIdEdited = false;
      this.isInternalPersonalIdFormSubmitted = false;
    }
  }

  cancelUpdate() {
    this.isInternalPersonalIdEdited = false;
    this.isInternalPersonalIdFormSubmitted = false;
    this.internalPersonalIdForm
      .get('internalPersonalId')
      ?.setValue(this.assignment.profile.customerProfilePersonalId || '');
  }

  onOrderChanged(key: number): void {
    if (this.endedAssignments?.length) {
      this.orderKey = key;
      this.orderDirection =
        !this.orderDirection || this.orderDirection === this.directions.desc
          ? this.directions.asc
          : this.directions.desc;
      this.orderEndedAssignments();
    }
  }

  orderEndedAssignments(): void {
    switch (this.orderKey) {
      case EndedAssignmentsListHeaderSortKeys.period:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return new Date(assignmentA.startDate!).getTime() - new Date(assignmentB.startDate!).getTime();
          } else {
            return new Date(assignmentB.startDate!).getTime() - new Date(assignmentA.startDate!).getTime();
          }
        });
        break;
      case EndedAssignmentsListHeaderSortKeys.department:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return assignmentA.customerDepartment!.name.localeCompare(assignmentB.customerDepartment!.name);
          } else {
            return assignmentB.customerDepartment!.name.localeCompare(assignmentA.customerDepartment!.name);
          }
        });
        break;
      case EndedAssignmentsListHeaderSortKeys.qualification:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return assignmentA.mainQualification!.name.localeCompare(assignmentB.mainQualification!.name);
          } else {
            return assignmentB.mainQualification!.name.localeCompare(assignmentA.mainQualification!.name);
          }
        });
        break;
    }
  }

  openRegenerateIDModal(): void {
    this.modalRef = this.modalService.open(
      {
        headText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalHeadText',
        bodyText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalBodyText',
        okText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalOkText',
      },
      {
        backdrop: 'static',
      }
    );

    this.modalRef.result.then(() => {
      this.store.dispatch(
        baseAssignmentsActions.regenerateCustomerAssignmentProfileIDCard({
          payload: { profile: this.assignment.profile.id, assignment: this.assignment.id },
        })
      );
    }, noop);
  }

  downloadIDCard(openInModal?: boolean, downloadOnly?: boolean): void {
    this.store.dispatch(
      baseAssignmentsActions.downloadCustomerAssignmentProfileIDCard({
        payload: { assignment: this.assignment.id, profile: this.assignment.profile.id, openInModal, downloadOnly },
      })
    );
  }

  private subscribeCustomerData(): void {
    this.store
      .select(baseAdministrationSelectors.getCustomerData)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as CustomerResponse)
      )
      .subscribe((customer) => {
        this.isTerminalFeatureActive = customer.timeRecordTerminalUsage;
      });
  }

  private subscribeProfileIDCard(): void {
    this.store
      .select(baseAssignmentsSelectors.getCustomerAssignmentProfileIDCard)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data)
      )
      .subscribe((idCardView) => {
        this.openIDCardViewer(idCardView.data as Blob, idCardView.fileName as string);
      });
  }

  private openIDCardViewer(document: Blob, fileName: string): void {
    this.activeDocumentSrc = document;
    this.idCardFileName = fileName;
    this.idCardModalRef = this.modalService.open(
      {
        bodyTemplate: this.idCardViewerTemplate,
        cancelText: 'action.close',
        cancelButtonOnly: true,
        okButtonDisplayed: false,
        hideHeader: true,
      },
      { size: 'md', backdrop: 'static' }
    );
    this.idCardModalRef.result.then(
      () => this.resetIDCardViewer(),
      () => this.resetIDCardViewer()
    );
  }

  private resetIDCardViewer(): void {
    this.store.dispatch(baseAssignmentsActions.clearCustomerAssignmentProfileIDCardView());
  }
}
